export const IMPORT_SETTING_KEYS = {
  DUPLICATE_POLICY: 'duplicate_policy',
  FILE_DUPLICATE_POLICY: 'file_duplicate_policy',
  MISSING_NAME_POLICY: 'missing_name_policy',
  MISSING_CONTACT_INFO_POLICY: 'missing_contact_info_policy',
};

const CONTACTS_SETTINGS_KEYS = {
  [IMPORT_SETTING_KEYS.FILE_DUPLICATE_POLICY]: {
    labelKey: 'ImporterSettingsFileDuplicatesLabel',
    descriptionKey: 'ImporterSettingsFileDuplicatesDescription',
    options: [
      {
        value: 'import_all',
        labelKey: 'ImporterSettingsFileDuplicatesImportAllLabel',
        descriptionKey: 'ImporterSettingsFileDuplicatesImportAllDescription',
      },
      {
        value: 'merge',
        labelKey: 'ImporterSettingsFileDuplicatesCombineLabel',
        descriptionKey: 'ImporterSettingsFileDuplicatesCombineDescription',
      },
      {
        value: 'keep_one',
        labelKey: 'ImporterSettingsFileDuplicatesImportOneLabel',
      },
    ],
  },
  [IMPORT_SETTING_KEYS.DUPLICATE_POLICY]: {
    labelKey: 'ImporterSettingsContactsExistingInDb',
    descriptionKey: 'ImporterSettingsContactsExistingInDbDescription',
    options: [
      {
        value: 'duplicate',
        labelKey: 'ImporterSettingsDbDuplicatesImportAnywayLabel',
        descriptionKey: 'ImporterSettingsDbDuplicatesImportAnywayDescription',
      },
      {
        value: 'update',
        labelKey: 'ImporterSettingsDbDuplicatesUpdateLabel',
        descriptionKey: 'ImporterSettingsDbDuplicatesUpdateDescription',
      },
      {
        value: 'ignore',
        labelKey: 'ImporterSettingsDbDuplicatesIgnoreLabel',
      },
    ],
  },
  [IMPORT_SETTING_KEYS.MISSING_NAME_POLICY]: {
    labelKey: 'ImporterSettingsMissingNameLabel',
    descriptionKey: '',
    options: [
      {
        value: 'import',
        labelKey: 'ImporterSettingsMissingNameImportAnywayLabel',
        descriptionKey: 'ImporterSettingsMissingNameImportAnywayDescription',
      },
      {
        value: 'ignore',
        labelKey: 'ImporterSettingsMissingNameIgnoreLabel',
      },
    ],
  },
  [IMPORT_SETTING_KEYS.MISSING_CONTACT_INFO_POLICY]: {
    labelKey: 'ImporterSettingsMissingContactMethodLabel',
    descriptionKey: '',
    options: [
      {
        value: 'import',
        labelKey: 'ImporterSettingsMissingContactMethodImportAnywayLabel',
        descriptionKey:
          'ImporterSettingsMissingContactMethodImportAnywayDescription',
      },
      {
        value: 'ignore',
        labelKey: 'ImporterSettingsMissingContactMethodIgnoreLabel',
      },
    ],
  },
};

const OWNERS_SETTINGS_KEYS = {
  [IMPORT_SETTING_KEYS.FILE_DUPLICATE_POLICY]: {
    labelKey: 'ImporterSettingsFileDuplicatesPropertyOwnersLabel',
    descriptionKey: 'ImporterSettingsFileDuplicatesPropertyOwnersDescription',
    options: [
      {
        value: 'import_all',
        labelKey: 'ImporterSettingsFileDuplicatesPropertyOwnersImportAllLabel',
        descriptionKey:
          'ImporterSettingsFileDuplicatesPropertyOwnersImportAllDescription',
      },
      {
        value: 'merge',
        labelKey: 'ImporterSettingsFileDuplicatesPropertyOwnersCombineLabel',
        descriptionKey:
          'ImporterSettingsFileDuplicatesPropertyOwnersCombineDescription',
      },
    ],
  },
  [IMPORT_SETTING_KEYS.DUPLICATE_POLICY]: {
    labelKey: 'ImporterSettingsDbDuplicatesPropertyOwnersLabel',
    descriptionKey: 'ImporterSettingsDbDuplicatesPropertyOwnersDescription',
    options: [
      {
        value: 'duplicate',
        labelKey: 'ImporterSettingsDbDuplicatesPropertyOwnersImportAnywayLabel',
        descriptionKey:
          'ImporterSettingsDbDuplicatesPropertyOwnersImportAnywayDescription',
      },
      {
        value: 'link',
        labelKey: 'ImporterSettingsDbDuplicatesPropertyOwnersLinkLabel',
        descriptionKey:
          'ImporterSettingsDbDuplicatesPropertyOwnersLinkDescription',
      },
    ],
  },
};

const SETTINGS_BY_ENTITY = {
  contacts: CONTACTS_SETTINGS_KEYS,
  owners: OWNERS_SETTINGS_KEYS,
};

const IMPORT_SETTINGS_ORDER = [
  IMPORT_SETTING_KEYS.FILE_DUPLICATE_POLICY,
  IMPORT_SETTING_KEYS.DUPLICATE_POLICY,
  IMPORT_SETTING_KEYS.MISSING_NAME_POLICY,
  IMPORT_SETTING_KEYS.MISSING_CONTACT_INFO_POLICY,
];

const buildImportSettings = (keysBySetting) =>
  IMPORT_SETTINGS_ORDER.filter((key) => keysBySetting[key]).map((key) => ({
    key,
    ...keysBySetting[key],
  }));

export const getImportSettings = (entity = 'contacts') =>
  buildImportSettings(SETTINGS_BY_ENTITY[entity] ?? {});

// Backward-compatible default for contacts.
export const IMPORT_SETTINGS = getImportSettings('contacts');

const IMPORT_SETTING_KEY_SET = new Set(Object.values(IMPORT_SETTING_KEYS));

const isPresentPolicyValue = (value) =>
  value != null && String(value).trim() !== '';

export const isImportSettingsComplete = (draft, entity = 'contacts') =>
  getImportSettings(entity).every((setting) =>
    isPresentPolicyValue(draft?.[setting.key])
  );

export const buildStartSettingsPayload = (draft = {}, entity = null) => {
  const allowedKeys = entity
    ? new Set(getImportSettings(entity).map((setting) => setting.key))
    : IMPORT_SETTING_KEY_SET;
  return Object.fromEntries(
    Object.entries(draft).filter(
      ([key, value]) => allowedKeys.has(key) && isPresentPolicyValue(value)
    )
  );
};

export const parseImportSettingsFromJob = (job = {}) => {
  const source = job.policies ?? job;
  return buildStartSettingsPayload({
    [IMPORT_SETTING_KEYS.DUPLICATE_POLICY]: source.duplicate_policy,
    [IMPORT_SETTING_KEYS.FILE_DUPLICATE_POLICY]: source.file_duplicate_policy,
    [IMPORT_SETTING_KEYS.MISSING_NAME_POLICY]: source.missing_name_policy,
    [IMPORT_SETTING_KEYS.MISSING_CONTACT_INFO_POLICY]:
      source.missing_contact_info_policy,
  });
};

const INVALID_IMPORT_POLICY_REASONS = new Set([
  'invalid_duplicate_policy',
  'invalid_file_duplicate_policy',
  'invalid_missing_name_policy',
  'invalid_missing_contact_info_policy',
]);

export const isInvalidImportPolicyReason = (reason) =>
  INVALID_IMPORT_POLICY_REASONS.has(reason);
